import { TimezoneInfo } from "@components/TimezoneInfo";
import { useInstitutionContext } from "@hooks/institutionContext";
import { useInboxMessageCountAsyncGenerator } from "@hooks/useInboxMessageCountAsyncGenerator";
import { useUserConfiguration } from "@hooks/useUserConfiguration";
import { IconButton, Tag, Tooltip } from "@narmi/design_system";
import Filters from "byzantine/src/filters";
import jsonFromDocument from "cerulean/src/JsonFromDocument";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { NavigationRoutes } from "./constants/navigationRoutes";
import { useRoutesEnabled } from "./hooks/useRoutesEnabled";
import { useStaffApps } from "./hooks/useStaffApps";
import styles from "./Navigation.module.css";
import { NavigationLink } from "./NavigationLink";
import { NavigationSection } from "./NavigationSection";
import { NavigationSubSection } from "./SubSection";
import { UserSettingsMenu } from "./UserSettingsMenu";

type SectionId =
  | "deposit_account_opening"
  | "users"
  | "engagement"
  | "money_movement"
  | "extensions"
  | "staff"
  | "configurations"
  | "conversions"
  | "account_opening_copy"
  | "lending";

/** `/naf/<uuid>/run` — launching an app, as opposed to managing apps under `/naf`. */
export function isStaffAppRunRoute(path: string) {
  return /^\/naf\/[^/]+\/run(\/|$)/.test(path);
}

function isRouteActive(route: NavigationRoutes, path: string) {
  // Apps is "/naf", which startsWith-matches every run URL.
  if (route === NavigationRoutes.Apps && isStaffAppRunRoute(path)) {
    return false;
  }
  if (route === NavigationRoutes.Dashboard) {
    // dashboard needs to match exactly since it's /
    return path === NavigationRoutes.Dashboard;
  }
  if (route == NavigationRoutes.ManageStaff) {
    return path.startsWith(route) && !path.startsWith(NavigationRoutes.StaffBulkUpload);
  }
  if (route == NavigationRoutes.ProductListManager) {
    return path.startsWith(route) && !path.startsWith(NavigationRoutes.ProductListManagerV2);
  }
  if (route == NavigationRoutes.Conversions) {
    return path.startsWith(route) && !path.startsWith(NavigationRoutes.ConversionDataViewer);
  }
  if (route == NavigationRoutes.RiskManager) {
    return path.startsWith(route) || path.startsWith(NavigationRoutes.RiskRules);
  }
  return path.startsWith(route);
}

const numberFormatter = new Intl.NumberFormat("en-US");

function useSafeNavigate() {
  // useNavigate() throws outside a <Router>; the sidebar also mounts standalone
  // on Django pages via navigation/main.tsx, so fall back to a full page load.
  try {
    return useNavigate();
  } catch {
    return (to: string) => window.location.assign(to);
  }
}

export function NavigationSidebar({ pathname }: { pathname: string }) {
  const { customerOrMember } = useInstitutionContext();

  const unreadMessageCount = useInboxMessageCountAsyncGenerator();

  const [collapsed, setCollapsed] = React.useState(
    window.localStorage.getItem("collapsed") === "true"
  );
  const [expandedId, setExpandedId] = React.useState<SectionId | null>(() => {
    if (
      isRouteActive(NavigationRoutes.DepositApplications, pathname) ||
      isRouteActive(NavigationRoutes.MembershipCardUploads, pathname) ||
      isRouteActive(NavigationRoutes.ProductListManager, pathname) ||
      isRouteActive(NavigationRoutes.ProductListManagerV2, pathname) ||
      isRouteActive(NavigationRoutes.Personas, pathname)
    ) {
      return "deposit_account_opening";
    }
    if (
      isRouteActive(NavigationRoutes.InstitutionSettings, pathname) ||
      isRouteActive(NavigationRoutes.InstitutionSettingsV2, pathname) ||
      isRouteActive(NavigationRoutes.RiskManager, pathname) ||
      isRouteActive(NavigationRoutes.CopyEditor, pathname) ||
      isRouteActive(NavigationRoutes.TestNotifications, pathname) ||
      isRouteActive(NavigationRoutes.TestCoreConnectivity, pathname) ||
      isRouteActive(NavigationRoutes.NotificationHistory, pathname) ||
      isRouteActive(NavigationRoutes.NarmiAdmin, pathname) ||
      isRouteActive(NavigationRoutes.SSO, pathname) ||
      isRouteActive(NavigationRoutes.ApplicationsConfiguration, pathname) ||
      isRouteActive(NavigationRoutes.MessagesConfiguration, pathname) ||
      isRouteActive(NavigationRoutes.SftpWizard, pathname) ||
      isRouteActive(NavigationRoutes.SettingsSync, pathname) ||
      isRouteActive(NavigationRoutes.WireFxRates, pathname)
    ) {
      return "configurations";
    }
    if (isRouteActive(NavigationRoutes.ManageUsers, pathname)) {
      return "users";
    }
    if (
      isRouteActive(NavigationRoutes.Audiences, pathname) ||
      isRouteActive(NavigationRoutes.Promotions, pathname) ||
      isRouteActive(NavigationRoutes.Guides, pathname) ||
      isRouteActive(NavigationRoutes.Survey, pathname)
    ) {
      return "engagement";
    }
    if (
      isRouteActive(NavigationRoutes.ACHManager, pathname) ||
      isRouteActive(NavigationRoutes.AOAchManager, pathname) ||
      isRouteActive(NavigationRoutes.PaymentsAchManager, pathname) ||
      isRouteActive(NavigationRoutes.PassthruAchManager, pathname) ||
      isRouteActive(NavigationRoutes.WireManager, pathname) ||
      isRouteActive(NavigationRoutes.IntlWireManager, pathname) ||
      isRouteActive(NavigationRoutes.FedNowManager, pathname)
    ) {
      return "money_movement";
    }
    if (
      isRouteActive(NavigationRoutes.Apps, pathname) ||
      isRouteActive(NavigationRoutes.OauthApps, pathname) ||
      isRouteActive(NavigationRoutes.Functions, pathname)
    ) {
      return "extensions";
    }
    if (
      isRouteActive(NavigationRoutes.ManageStaff, pathname) ||
      isRouteActive(NavigationRoutes.ManageRoles, pathname) ||
      isRouteActive(NavigationRoutes.StaffBulkUpload, pathname)
    ) {
      return "staff";
    }
    if (
      isRouteActive(NavigationRoutes.Conversions, pathname) ||
      isRouteActive(NavigationRoutes.Deconversions, pathname) ||
      isRouteActive(NavigationRoutes.ConversionDataViewer, pathname)
    ) {
      return "conversions";
    }
    return null;
  });
  const [showConfigurations, setShowConfigurations] = React.useState(
    expandedId === "configurations" // only if the default state is configurations.
  );
  const showConversionPortal = pathname.startsWith("/conversion_portal");
  const mouseDownRef = React.useRef(false);
  // Don't use useNavigate directly because it will throw outside a <Router> (the sidebar also mounts standalone on Django pages via navigation/main.tsx), so fall back to a full page load.
  const safeNavigate = useSafeNavigate();

  const enabled = useRoutesEnabled();
  const staffAppsBySection = useStaffApps();

  // The configured section is only known once entries load.
  React.useEffect(() => {
    if (!isStaffAppRunRoute(pathname)) return;
    for (const [section, apps] of Object.entries(staffAppsBySection)) {
      if (apps.some((app) => pathname.startsWith(`/naf/${app.uuid}/run`))) {
        setExpandedId(section as SectionId);
        return;
      }
    }
  }, [pathname, staffAppsBySection]);

  // Rendered after a section's built-in items, using the app's registered name and icon.
  function staffAppLinks(section: SectionId) {
    return (staffAppsBySection[section] || []).map((app) => {
      const to = `/naf/${app.uuid}/run`;
      return (
        <NavigationLink
          key={app.uuid}
          to={to}
          highlighted={pathname.startsWith(to)}
          compact={collapsed}
          preserveIdentity
          icon={
            app.image ? (
              <i
                style={{ color: "#4c4c4c" }}
                className={`${styles.navigationLinkIcon} narmi-icon-${app.image}`}
              />
            ) : null
          }
        >
          <div className={styles.navigationLinkContent}>
            <div>{app.name}</div>
          </div>
        </NavigationLink>
      );
    });
  }

  // this rare, embedded component is here because we want the `<NavLink />` components to be a single line to
  // avoid this file exploding in size and make it easier to read.
  type NavLinkProps = {
    to: keyof typeof NavigationRoutes;
    icon?: string;
    text: string; // this is also not `children` to keep the call site short.
    secondaryText?: number | string;
    newUntil?: string; // ISO 8601 date string
  };
  function navLink({ to, icon, text, newUntil, secondaryText }: NavLinkProps) {
    const route = NavigationRoutes[to];
    const isNew = newUntil && new Date() < new Date(newUntil);
    return (
      enabled[route] && (
        <NavigationLink
          to={route}
          highlighted={isRouteActive(route, pathname)}
          icon={
            icon ? (
              <i
                style={{ color: "#4c4c4c" }}
                className={`${styles.navigationLinkIcon} ${isNew && collapsed ? styles.newDot : ""} narmi-icon-${icon}`}
              />
            ) : null
          }
          compact={collapsed}
        >
          <div className={styles.navigationLinkContent}>
            <div>
              {text}
              {isNew && !collapsed && (
                <div className={styles.newTag}>
                  <Tag kind="subdued-secondary" label="New" />
                </div>
              )}
            </div>
            {secondaryText ? <div className={styles.secondaryText}>{secondaryText}</div> : null}
          </div>
        </NavigationLink>
      )
    );
  }

  const expandedHandler = (id: SectionId) => {
    return {
      expanded: id === expandedId,
      onMouseDown: () => {
        mouseDownRef.current = true; // this is to prevent the `onFocus` event from firing after a `onMouseDown` event
        if (id === expandedId) {
          setExpandedId(null);
        } else {
          setExpandedId(id);
        }
      },
      onFocus: () => {
        if (mouseDownRef.current) {
          mouseDownRef.current = false;
          return;
        }
        setExpandedId(id);
      },
    };
  };

  if (showConversionPortal && !collapsed) {
    return (
      <NavigationConfigurationsSidebar
        onBackClick={() => safeNavigate(NavigationRoutes.Dashboard)}
        header="Back"
      >
        <NavigationText icon="pie-chart">Conversion portal</NavigationText>
        {navLink({ to: "ConversionPortalHome", text: "Home" })}
        {navLink({ to: "ConversionPortalExclusions", text: "Exclusions" })}
        {navLink({ to: "ConversionPortalIssues", text: "Issues" })}
        {navLink({ to: "ConversionPortalUsers", text: "Users" })}
      </NavigationConfigurationsSidebar>
    );
  }

  // show expanded configurations sidebar the side nav is not collapsed.
  if (showConfigurations && !collapsed) {
    return (
      <NavigationConfigurationsSidebar
        onBackClick={() => setShowConfigurations(false)}
        header="Back"
      >
        <NavigationText icon="settings-gear">Configurations</NavigationText>
        {navLink({ to: "InstitutionSettingsV2", text: "Institution settings" })}
        {navLink({ to: "InstitutionSettings", text: "Institution settings" })}
        {navLink({ to: "MessagesConfiguration", text: "Messages" })}
        {navLink({ to: "ApplicationsConfiguration", text: "Applications" })}
        {navLink({ to: "SSO", text: "SSO" })}
        {navLink({ to: "RiskManager", text: "Risk manager" })}
        {navLink({ to: "TestNotifications", text: "Test notifications" })}
        {navLink({ to: "TestCoreConnectivity", text: "Test core connectivity" })}
        {navLink({ to: "NotificationHistory", text: "Notification history" })}
        {navLink({ to: "SftpWizard", text: "SFTP Wizard" })}
        {navLink({ to: "SettingsSync", text: "Settings sync" })}
        {navLink({ to: "NarmiAdmin", text: "Narmi admin" })}
        {navLink({ to: "Preburner", icon: "sparkle", text: "Preburner" })}
        {navLink({ to: "WireFxRates", text: "Wire FX rates" })}
        <NavigationText icon="edit">Copy Editor</NavigationText>
        {navLink({ to: "CopyEditorSettings", text: "Global Messages" })}
        {navLink({ to: "CopyEditorServer", text: "Server Messages" })}
        {navLink({ to: "CopyEditorOlb", text: "Online Banking" })}
        <NavigationSubSection
          header="Account opening"
          compact={collapsed}
          {...expandedHandler("account_opening_copy")}
          overrideSingleChildHeader
        >
          {navLink({ to: "CopyEditorCao", text: "Consumer" })}
          {navLink({ to: "CopyEditorBao", text: "Business" })}
          {navLink({ to: "CopyEditorSao", text: "Staffer-led" })}
        </NavigationSubSection>
        {navLink({ to: "CopyEditorApp", text: "Mobile App" })}
        {navLink({ to: "CopyEditorLending", text: "Lending" })}
      </NavigationConfigurationsSidebar>
    );
  }

  return (
    <NavigationGlobalSidebar
      compact={collapsed}
      onCollapse={() => {
        setCollapsed(!collapsed);
        window.localStorage.setItem("collapsed", String(!collapsed));
      }}
      actions={!collapsed && <UserSettingsMenu />}
    >
      {navLink({ to: "Dashboard", icon: "pie-chart", text: "Dashboard" })}
      {navLink({
        to: "Inbox",
        icon: "inbox",
        text: "Inbox",
        secondaryText:
          unreadMessageCount < 100 ? numberFormatter.format(unreadMessageCount) : "99+",
      })}
      {enabled[NavigationRoutes.ConversionPortalHome] &&
        navLink({
          to: "ConversionPortalHome",
          icon: "pie-chart",
          text: "Conversion portal",
        })}
      {navLink({ to: "Messages", icon: "message-square", text: "Messages" })}
      <NavigationSection
        icon="book-open"
        header={enabled[NavigationRoutes.LoanApplications] ? "Deposits" : "Account opening"}
        compact={collapsed}
        {...expandedHandler("deposit_account_opening")}
      >
        {navLink({ to: "DepositApplications", text: "Applications" })}
        {navLink({ to: "ProductListManager", text: "Product list manager" })}
        {navLink({ to: "ProductListManagerV2", text: "Product list manager (v2)" })}
        {navLink({ to: "Personas", text: "Personas" })}
        {navLink({ to: "MembershipCardUploads", text: "Membership card uploads" })}
        {staffAppLinks("deposit_account_opening")}
      </NavigationSection>
      {navLink({
        to: "LoanApplications",
        icon: "loan",
        text: enabled[NavigationRoutes.DepositApplications] ? "Loans" : "Lending",
      })}
      {navLink({ to: "ManageUsers", icon: "user", text: `${Filters.humanize(customerOrMember)}s` })}
      <NavigationSection
        icon="share-2"
        header="Engagement"
        compact={collapsed}
        {...expandedHandler("engagement")}
      >
        {navLink({ to: "Audiences", text: "Audiences" })}
        {navLink({ to: "Promotions", text: "Promotions" })}
        {navLink({ to: "Guides", text: "Guides" })}
        {navLink({ to: "Survey", text: "Surveys" })}
        {staffAppLinks("engagement")}
      </NavigationSection>
      <NavigationSection
        icon="transfer-arrows"
        header="Money movement"
        compact={collapsed}
        {...expandedHandler("money_movement")}
        overrideSingleChildHeader
      >
        <NavigationSubSection
          header="ACH manager"
          expanded={
            isRouteActive(NavigationRoutes.ACHManager, pathname) ||
            isRouteActive(NavigationRoutes.AOAchManager, pathname) ||
            isRouteActive(NavigationRoutes.PaymentsAchManager, pathname) ||
            isRouteActive(NavigationRoutes.PassthruAchManager, pathname)
          }
        >
          {navLink({ to: "AOAchManager", text: "Account opening" })}
          {navLink({ to: "ACHManager", text: "Transfers" })}
          {navLink({ to: "PaymentsAchManager", text: "Payments" })}
          {navLink({ to: "PassthruAchManager", text: "Pass-thru" })}
        </NavigationSubSection>
        <NavigationSubSection header="Wire manager">
          {navLink({ to: "WireManager", text: "Domestic wires" })}
          {navLink({
            to: "IntlWireManager",
            text: "International wires",
          })}
        </NavigationSubSection>
        {navLink({ to: "FedNowManager", text: "FedNow manager" })}
        {staffAppLinks("money_movement")}
      </NavigationSection>
      {navLink({ to: "Guard", icon: "shield", text: "Guard" })}
      {navLink({ to: "Reports", icon: "bar-chart", text: "Reports" })}
      <NavigationSection
        icon="tool"
        header="Conversion tools"
        compact={collapsed}
        {...expandedHandler("conversions")}
      >
        {navLink({ to: "Conversions", text: "Conversions" })}
        {navLink({ to: "ConversionDataViewer", text: "Conversion Data Viewer" })}
        {navLink({ to: "Deconversions", text: "Deconversions" })}
        {staffAppLinks("conversions")}
      </NavigationSection>
      <NavigationSection
        icon="code"
        header="Extensions"
        compact={collapsed}
        {...expandedHandler("extensions")}
      >
        {navLink({ to: "Apps", text: "NAF Apps" })}
        {navLink({ to: "OauthApps", text: "OAuth Applications" })}
        {navLink({ to: "Functions", text: "Narmi Functions" })}
        {staffAppLinks("extensions")}
      </NavigationSection>
      <NavigationSection
        icon="admin-user-2"
        header="Staff members"
        compact={collapsed}
        {...expandedHandler("staff")}
      >
        {navLink({ to: "ManageStaff", text: "Staff" })}
        {navLink({ to: "ManageRoles", text: "Roles" })}
        {navLink({ to: "StaffBulkUpload", text: "Staff bulk upload" })}
        {staffAppLinks("staff")}
      </NavigationSection>
      {collapsed ? (
        <>
          {navLink({ to: "InstitutionSettingsV2", icon: "settings", text: "Institution settings" })}
          {navLink({ to: "InstitutionSettings", icon: "settings", text: "Institution settings" })}
        </>
      ) : (
        <NavigationSection
          icon="settings"
          header="Configurations"
          compact={collapsed}
          onMouseDown={() => setShowConfigurations(true)}
          onFocus={() => setShowConfigurations(true)}
        />
      )}
    </NavigationGlobalSidebar>
  );
}

export function NavigationGlobalSidebar({
  actions,
  onCollapse,
  compact = false,
  children,
}: React.PropsWithChildren<{
  compact?: boolean;
  onCollapse: () => void;
  actions?: React.ReactNode;
}>) {
  const [showOpenDrawerIcon, setShowOpenDrawerIcon] = useState(false);
  const { roles } = useUserConfiguration();
  const { institution } = useInstitutionContext();

  const favicon = React.useMemo(() => {
    const nodeList = document.getElementsByTagName("link");
    for (let i = 0; i < nodeList.length; i++) {
      if (
        nodeList[i].getAttribute("rel") == "icon" ||
        nodeList[i].getAttribute("rel") == "shortcut icon"
      ) {
        return nodeList[i].getAttribute("href");
      }
    }
  }, []);

  if (compact) {
    return (
      // the global styles navigation-sidebar and navigation-sidebar-collapsed
      // are used to signal to position: fixed elements in the page content that
      // there is a navigation element present.
      //
      // See FloatingSnackbar.module.css
      <nav
        className={`${styles.navigationSidebar} ${styles.collapsed} navigation-sidebar-collapsed`}
        onMouseEnter={() => setShowOpenDrawerIcon(true)}
        onMouseLeave={() => setShowOpenDrawerIcon(false)}
      >
        {!showOpenDrawerIcon && (
          <div className={styles.navigationSidebarHeader}>
            <div className={styles.navigationSidebarHeaderLogo}>
              {favicon ? (
                <img src={favicon} alt="Narmi Logo" />
              ) : (
                <i className="narmi-icon-bank"></i>
              )}
            </div>
          </div>
        )}
        {showOpenDrawerIcon && (
          <div className={styles.navigationSidebarCompactHeader}>
            <IconButton
              kind="action"
              label="Help"
              name="chevrons-right"
              onClick={() => {
                onCollapse();
                setShowOpenDrawerIcon(false);
              }}
            />
          </div>
        )}

        <div className={styles.navigationSection}>{children}</div>
        <div className={styles.infoFooter}>
          <IconButton
            kind="plain"
            label="Help"
            name="info"
            onClick={() => window.open("https://help.narmi.com", "_blank")}
          />
        </div>
      </nav>
    );
  }

  return (
    <nav className={`${styles.navigationSidebar} navigation-sidebar`}>
      <div className={styles.navigationSidebarHeader}>
        <div className={styles.navigationSidebarHeaderLogo}>
          {favicon ? <img src={favicon} alt="Narmi Logo" /> : <i className="narmi-icon-bank"></i>}
        </div>
        <div className={styles.navigationSidebarHeaderText}>
          {jsonFromDocument("institution_long_name")}
        </div>
        <Tooltip text="Close sidebar" side="bottom">
          <div>
            <IconButton kind="action" label="Help" name="chevrons-left" onClick={onCollapse} />
          </div>
        </Tooltip>
        {actions}
      </div>
      <div className={styles.navigationSection}>{children}</div>
      <div className={styles.infoFooter}>
        <IconButton
          kind="plain"
          label="Help"
          name="info"
          onClick={() => window.open("https://help.narmi.com", "_blank")}
        />
        <div>{institution.release_version}</div>
      </div>
      {roles.narmi && <TimezoneInfo />}
    </nav>
  );
}

export function NavigationConfigurationsSidebar({
  onBackClick,
  header,
  children,
}: React.PropsWithChildren<{
  onBackClick: () => void;
  header: string;
  actions?: React.ReactNode;
}>) {
  const { roles } = useUserConfiguration();
  const { institution } = useInstitutionContext();
  return (
    <nav className={styles.navigationSidebar}>
      <div className={styles.navigationSidebarConfigurationsHeader} onClick={onBackClick}>
        <IconButton kind="plain" label="Back" name="chevron-left" />
        <div className={styles.navigationSidebarHeaderText}>{header}</div>
      </div>
      <div className={styles.navigationSection}>{children}</div>
      <div className={styles.infoFooter}>
        <IconButton
          kind="plain"
          label="Help"
          name="info"
          onClick={() => window.open("https://help.narmi.com", "_blank")}
        />
        <div>{institution.release_version}</div>
      </div>
      {roles.narmi && <TimezoneInfo />}
    </nav>
  );
}

export function NavigationText({
  icon,
  children,
}: React.PropsWithChildren<{
  icon?: string;
}>) {
  return (
    <div className={styles.navigationLink}>
      {icon && <i className={`${styles.navigationLinkIcon} narmi-icon-${icon}`} />}
      <div className={`${styles.navigationLinkText} whiteSpace--truncate`}>{children}</div>
    </div>
  );
}
